import { classNames } from "helpers";
import React, { useCallback, useState } from "react";

import { initialPassMinRequirements, initialPassMustContain } from "./consts";
import { PasswordFieldProps } from "./types";
import { validateRequirements } from "./validation";

const PasswordField: React.FC<PasswordFieldProps> = (props) => {
  const {
    id,
    value,
    onChange,
    name,
    label,
    placeholder,
    readOnly,
    disabled,
    required,
    error,
    isPasswordValid,
    showRequirements,
    width,
    height = "normal",
    cssClassName,
    ...otherProps
  } = props;

  const [passMinRequirements, setPassMinRequirements] = useState(
    initialPassMinRequirements
  );
  const [passMustContain, setPassMustContain] = useState(
    initialPassMustContain
  );
  // Pratimo je li korisnik ikada interaktirao s poljem (focus/type) da bi izbjegli
  // crveno error stanje pri početnom render-u dok polje još nije ni dotaknuto.
  const [hasInteracted, setHasInteracted] = useState(false);

  // Crveno pokaži samo: (1) ako je proslijeđen `error` prop (parent validacija) ili
  // (2) ako je korisnik već interaktirao i lozinka još uvijek nije validna i prazna je.
  const isError = !!error || (hasInteracted && !isPasswordValid && !value);

  const handlePasswordChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const currentValue = ev.target.value ? ev.target.value : null;

      const isPassMinValid = validateRequirements(
        currentValue,
        passMinRequirements,
        setPassMinRequirements
      );
      const isPassMustValid = validateRequirements(
        currentValue,
        passMustContain,
        setPassMustContain
      );

      onChange(ev, isPassMinValid && isPassMustValid);
    },
    [onChange, passMinRequirements, passMustContain]
  );

  const handleFocus = useCallback(() => setHasInteracted(true), []);

  const widthClassName = () => {
    switch (width) {
      case "min":
        return "w-24";
      case "small":
        return "w-48";
      case "medium":
        return "w-96";
      case "large":
        return "w-full";
      case "full":
        return "w-full";
      default:
        return "w-full";
    }
  };

  const heightClassName = () => {
    switch (height) {
      case "small":
        return "py-1.5";
      case "normal":
        return "py-2.5";
      case "big":
        return "py-3.5";
      default:
        return "py-2.5";
    }
  };

  const commonClassName = () => {
    return classNames("relative cursor-default rounded-sm pl-3 pr-3 text-left text-gray-800 placeholder:text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-slate-400 sm:text-sm sm:leading-6 z-0 sm:text-sm sm:leading-6 disabled:cursor-default disabled:bg-gray-100 disabled:text-gray-900 disabled:border-gray-300 disabled:shadow-none", heightClassName());
  };

  const errorClassName = () => {
    if (isError && required) {
      // `focus:!ring-red-500` jer bi inače focus ring iz commonClassName pregazio crveni obrub.
      return "bg-red-50 ring-1 ring-red-500 text-red-900 placeholder-red-700 focus:!ring-red-500";
    } else {
      return "ring-1 ring-slate-300 bg-slate-50";
    }
  };



  return (
    <>
      {/* Bez labele nema ni zvjezdice — inače ostane visjeti iznad polja kad naslov
          piše sam ekran (npr. prijava). */}
      {label && (
        <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <input
        type="password"
        name={name}
        disabled={disabled}
        onChange={handlePasswordChange}
        onFocus={handleFocus}
        className={classNames(commonClassName(), widthClassName(), errorClassName(), cssClassName)}
        placeholder={placeholder}
        required
        {...otherProps}
      />
      {showRequirements && (
        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm  p-4">
          <div className="mb-2">
            <strong className="block mb-2 text-sm font-medium text-gray-800">
              Minimalni uvijeti:
            </strong>
            {passMinRequirements.map((p, index) => (
              <div key={index} className="flex items-center  ml-2">
                {p.isValid ? (
                  <span className="w-1.5 h-1.5 block rounded-full mr-1 bg-green-600"></span>
                ) : (
                  <span className="w-1.5 h-1.5 block rounded-full mr-1 bg-red-600"></span>
                )}
                <p className="opacity-80 text-sm ml-1">{p.message}</p>
              </div>
            ))}
          </div>
          <div>
            <strong className="block mb-2 text-sm font-medium text-gray-800">
              Lozinka mora sadržavati:
            </strong>
            {passMustContain.map((p, index) => (
              <div key={index} className="flex items-center  ml-2">
                {p.isValid ? (
                  <span className="w-1.5 h-1.5 block rounded-full mr-1 bg-green-600"></span>
                ) : (
                  <span className="w-1.5 h-1.5 block rounded-full mr-1 bg-red-600"></span>
                )}
                <p className="opacity-80 text-sm  ml-1">{p.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(PasswordField);
